import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import {
  fetchProfile,
  forgotPasswordUser,
  appleLoginUser,
  googleLoginUser,
  loginUser,
  logoutUser,
  requestLoginOtpUser,
  resendOtpUser,
  changePasswordUser,
  resetPasswordUser,
  signupUser,
  updateProfileUser,
  verifyOtpUser,
} from "@/src/redux/actions/authActions";
import type { AuthState, PendingVerification } from "@/src/types/auth.types";
import { clearPropertyOnboardingSkipped } from "@/src/utils/resolvePostAuthDestination";
import {
  clearAccessTokenCookie,
  clearPendingVerification,
  setAccessTokenCookie,
  persistPendingVerification,
} from "@/src/utils/storage";

const initialState: AuthState = {
  accessToken: null,
  refreshToken: null,
  user: null,
  isAuthenticated: false,
  loading: false,
  error: null,
  pendingVerification: null,
};

function applyAuthenticatedSession(
  state: AuthState,
  payload: {
    user: AuthState["user"];
    accessToken: string;
    refreshToken: string;
  },
): void {
  state.accessToken = payload.accessToken;
  state.refreshToken = payload.refreshToken;
  state.user = payload.user;
  state.isAuthenticated = true;
  state.pendingVerification = null;
  state.error = null;
  setAccessTokenCookie(payload.accessToken);
  clearPendingVerification();
}

function clearAuthSession(state: AuthState): void {
  state.accessToken = null;
  state.refreshToken = null;
  state.user = null;
  state.isAuthenticated = false;
  state.error = null;
  state.pendingVerification = null;
  clearAccessTokenCookie();
  clearPendingVerification();
}

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearAuthError(state) {
      state.error = null;
    },
    setAuthError(state, action: PayloadAction<string>) {
      state.error = action.payload;
    },
    setPendingVerification(state, action: PayloadAction<PendingVerification>) {
      state.pendingVerification = action.payload;
      persistPendingVerification(action.payload);
    },
    hydratePendingVerification(state, action: PayloadAction<PendingVerification | null>) {
      state.pendingVerification = action.payload;
    },
    forceLogout(state) {
      clearAuthSession(state);
    },
    setSessionTokens(
      state,
      action: PayloadAction<{ accessToken: string; refreshToken: string }>,
    ) {
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
      state.isAuthenticated = true;
      setAccessTokenCookie(action.payload.accessToken);
    },
    clearStaleVerification(state) {
      state.pendingVerification = null;
      state.error = null;
      clearPendingVerification();
    },
  },
  extraReducers: (builder) => {
    const setLoading = (state: AuthState) => {
      state.loading = true;
      state.error = null;
    };
    const setIdle = (state: AuthState) => {
      state.loading = false;
    };

    builder
      .addCase(signupUser.pending, setLoading)
      .addCase(signupUser.fulfilled, (state, action) => {
        setIdle(state);
        // New accounts must see property setup after OTP (ignore prior "skip" in this browser).
        clearPropertyOnboardingSkipped();
        state.pendingVerification = {
          userId: action.payload.userId,
          email: action.payload.email,
          purpose: "signup",
        };
        persistPendingVerification(state.pendingVerification);
      })
      .addCase(signupUser.rejected, (state, action) => {
        setIdle(state);
        state.error = (action.payload as string) ?? "Signup failed";
      })

      .addCase(loginUser.pending, setLoading)
      .addCase(loginUser.fulfilled, (state, action) => {
        setIdle(state);
        const result = action.payload;
        if (result.requiresVerification && result.pendingUserId) {
          state.isAuthenticated = false;
          state.accessToken = null;
          state.refreshToken = null;
          if (result.user) {
            state.user = result.user;
          }
          state.pendingVerification = {
            userId: result.pendingUserId,
            email: result.pendingEmail ?? result.user?.email ?? "",
          };
          persistPendingVerification(state.pendingVerification);
          return;
        }
        if (result.user && result.accessToken && result.refreshToken) {
          applyAuthenticatedSession(state, {
            user: result.user,
            accessToken: result.accessToken,
            refreshToken: result.refreshToken,
          });
        }
      })
      .addCase(requestLoginOtpUser.pending, setLoading)
      .addCase(requestLoginOtpUser.fulfilled, (state, action) => {
        setIdle(state);
        state.pendingVerification = {
          userId: action.payload.userId,
          email: action.payload.email,
          purpose: "login",
        };
        persistPendingVerification(state.pendingVerification);
      })
      .addCase(requestLoginOtpUser.rejected, (state, action) => {
        setIdle(state);
        state.error = (action.payload as string) ?? "Could not send verification code";
      })

      .addCase(loginUser.rejected, (state, action) => {
        setIdle(state);
        state.error = (action.payload as string) ?? "Login failed";
      })

      .addCase(googleLoginUser.pending, setLoading)
      .addCase(googleLoginUser.fulfilled, (state, action) => {
        setIdle(state);
        const result = action.payload;
        if (result.user && result.accessToken && result.refreshToken) {
          applyAuthenticatedSession(state, {
            user: result.user,
            accessToken: result.accessToken,
            refreshToken: result.refreshToken,
          });
        }
      })
      .addCase(googleLoginUser.rejected, (state, action) => {
        setIdle(state);
        state.error = (action.payload as string) ?? "Google sign-in failed";
      })

      .addCase(appleLoginUser.pending, setLoading)
      .addCase(appleLoginUser.fulfilled, (state, action) => {
        setIdle(state);
        const result = action.payload;
        if (result.user && result.accessToken && result.refreshToken) {
          applyAuthenticatedSession(state, {
            user: result.user,
            accessToken: result.accessToken,
            refreshToken: result.refreshToken,
          });
        }
      })
      .addCase(appleLoginUser.rejected, (state, action) => {
        setIdle(state);
        state.error = (action.payload as string) ?? "Apple sign-in failed";
      })

      .addCase(verifyOtpUser.pending, setLoading)
      .addCase(verifyOtpUser.fulfilled, (state, action) => {
        setIdle(state);
        applyAuthenticatedSession(state, action.payload);
      })
      .addCase(verifyOtpUser.rejected, (state, action) => {
        setIdle(state);
        state.error = (action.payload as string) ?? "Verification failed";
      })

      .addCase(resendOtpUser.pending, setLoading)
      .addCase(resendOtpUser.fulfilled, (state) => {
        setIdle(state);
        state.error = null;
      })
      .addCase(resendOtpUser.rejected, (state, action) => {
        setIdle(state);
        state.error = (action.payload as string) ?? "Could not resend code";
      })

      .addCase(forgotPasswordUser.pending, setLoading)
      .addCase(forgotPasswordUser.fulfilled, (state) => {
        setIdle(state);
        state.error = null;
      })
      .addCase(forgotPasswordUser.rejected, (state, action) => {
        setIdle(state);
        state.error = (action.payload as string) ?? "Request failed";
      })

      .addCase(resetPasswordUser.pending, setLoading)
      .addCase(resetPasswordUser.fulfilled, (state) => {
        setIdle(state);
        state.error = null;
      })
      .addCase(resetPasswordUser.rejected, (state, action) => {
        setIdle(state);
        state.error = (action.payload as string) ?? "Reset failed";
      })

      .addCase(changePasswordUser.pending, setLoading)
      .addCase(changePasswordUser.fulfilled, (state) => {
        setIdle(state);
        state.error = null;
      })
      .addCase(changePasswordUser.rejected, (state, action) => {
        setIdle(state);
        state.error = (action.payload as string) ?? "Password update failed";
      })

      .addCase(logoutUser.pending, setLoading)
      .addCase(logoutUser.fulfilled, (state) => {
        setIdle(state);
        clearAuthSession(state);
      })
      .addCase(logoutUser.rejected, (state) => {
        setIdle(state);
        clearAuthSession(state);
      })

      .addCase(fetchProfile.pending, setLoading)
      .addCase(fetchProfile.fulfilled, (state, action) => {
        setIdle(state);
        state.user = action.payload;
        state.isAuthenticated = Boolean(state.accessToken);
      })
      .addCase(fetchProfile.rejected, (state, action) => {
        setIdle(state);
        state.error = (action.payload as string) ?? "Failed to load profile";
      })

      .addCase(updateProfileUser.pending, (state) => {
        state.error = null;
      })
      .addCase(updateProfileUser.fulfilled, (state, action) => {
        state.user = action.payload;
        state.isAuthenticated = Boolean(state.accessToken);
        state.error = null;
      });
  },
});

export const {
  clearAuthError,
  setAuthError,
  setPendingVerification,
  hydratePendingVerification,
  forceLogout,
  setSessionTokens,
  clearStaleVerification,
} = authSlice.actions;

export const authReducer = authSlice.reducer;
